import { VentasResumenProductoRow } from '../services/ventasInformesService';
import {
  CampoClasificacion,
  isSameProductoResumenRow,
  normalizarClasificacion,
} from './agruparVentasResumenProducto';

export type ResumenProductoMes = {
  key: string;
  label: string;
  rows: VentasResumenProductoRow[];
  hasRealMonth: boolean;
};

export type SerieBarraMes = {
  nombre: string;
  monto: number;
};

/** Clave del eje X en el gráfico agrupado (evitar colisión con un ramo llamado "mes"). */
export const CHART_MES_KEY = 'mesLabel';

export type ChartRowMensual = {
  [CHART_MES_KEY]: string;
  [serie: string]: string | number;
};

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const CHART_COLORS = [
  '#1976d2',
  '#2e7d32',
  '#ed6c02',
  '#6a1b9a',
  '#00838f',
  '#c62828',
  '#4527a0',
  '#558b2f',
  '#ef6c00',
  '#546e7a',
];

const OTROS_LABEL = 'Otros';
const RESTO_LABEL = 'Resto';

export function chartColorAt(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length];
}

function padMonth(year: string | number, month: number): string | null {
  if (!Number.isFinite(month) || month < 1 || month > 12) return null;
  const y = String(year);
  if (!/^\d{4}$/.test(y)) return null;
  return `${y}-${String(month).padStart(2, '0')}`;
}

/**
 * Normaliza strings de mes a YYYY-MM.
 * Evita `new Date` sobre valores ambiguos ("7", "202601") que JS interpreta mal.
 */
export function normalizeMesKey(mes?: string | number | null): string | null {
  if (mes == null) return null;
  const raw = String(mes).trim();
  if (!raw) return null;

  // YYYY-MM o YYYY-M
  let m = raw.match(/^(\d{4})-(\d{1,2})$/);
  if (m) return padMonth(m[1], Number(m[2]));

  // YYYYMM
  m = raw.match(/^(\d{4})(\d{2})$/);
  if (m) return padMonth(m[1], Number(m[2]));

  // MM/YYYY o M-YYYY
  m = raw.match(/^(\d{1,2})[/.-](\d{4})$/);
  if (m) return padMonth(m[2], Number(m[1]));

  // ISO / fecha con día: 2026-07-15 o 2026-07-15T00:00:00
  m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return padMonth(m[1], Number(m[2]));

  return null;
}

function fmtMonthLabel(key: string): string {
  const match = key.match(/^(\d{4})-(\d{2})$/);
  if (!match) return key === 'periodo' ? 'Período' : key;
  const monthIdx = Number(match[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return key;
  return `${MONTH_NAMES[monthIdx]} ${match[1]}`;
}

export function groupRowsByMes(rows: VentasResumenProductoRow[]): ResumenProductoMes[] {
  const map = new Map<string, VentasResumenProductoRow[]>();
  let anyReal = false;

  for (const r of rows) {
    const key = normalizeMesKey(r.mes);
    if (key) {
      anyReal = true;
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    } else {
      const list = map.get('periodo') ?? [];
      list.push(r);
      map.set('periodo', list);
    }
  }

  if (map.size === 0) return [];

  if (!anyReal && map.size === 1 && map.has('periodo')) {
    return [
      {
        key: 'periodo',
        label: 'Período',
        rows: map.get('periodo') ?? [],
        hasRealMonth: false,
      },
    ];
  }

  // Si hay meses reales, no mezclar filas sin mes en un bucket aparte engañoso:
  // sumarlas al período solo si no hubo ningún mes; si hubo, descartar del eje temporal
  // y dejarlas fuera de los buckets mensuales (ya están en tablas vía agregación total).
  if (anyReal) {
    map.delete('periodo');
  }

  const keys = Array.from(map.keys()).sort((a, b) => a.localeCompare(b));

  return keys.map((key) => ({
    key,
    label: fmtMonthLabel(key),
    rows: map.get(key) ?? [],
    hasRealMonth: key !== 'periodo',
  }));
}

function topNConOtros(
  entries: { nombre: string; monto: number }[],
  topN: number
): { items: SerieBarraMes[]; syntheticLabel: string | null } {
  const sorted = [...entries].sort((a, b) => Math.abs(b.monto) - Math.abs(a.monto));
  if (sorted.length <= topN) {
    return { items: sorted, syntheticLabel: null };
  }

  const top = sorted.slice(0, topN);
  const otrosMonto = sorted.slice(topN).reduce((acc, e) => acc + e.monto, 0);
  if (Math.abs(otrosMonto) <= 1e-9) {
    return { items: top, syntheticLabel: null };
  }

  const nombresTop = new Set(top.map((e) => e.nombre));
  const syntheticLabel =
    nombresTop.has(OTROS_LABEL) || nombresTop.has(RESTO_LABEL) ? RESTO_LABEL : OTROS_LABEL;

  // Si ya hay una categoría real con ese nombre, acumular el resto ahí.
  const existing = top.find((e) => e.nombre === syntheticLabel);
  if (existing) {
    existing.monto += otrosMonto;
    return { items: top, syntheticLabel };
  }

  top.push({ nombre: syntheticLabel, monto: otrosMonto });
  return { items: top, syntheticLabel };
}

function montosPorCampo(rows: VentasResumenProductoRow[], field: CampoClasificacion): Map<string, number> {
  const map = new Map<string, number>();
  for (const r of rows) {
    const nombre = normalizarClasificacion(r[field]);
    map.set(nombre, (map.get(nombre) ?? 0) + r.montoTotal);
  }
  return map;
}

/** Barras agrupadas: filas = meses, columnas = ramos (top N + Otros). */
export function buildSerieRamoAgrupada(
  rowsMensuales: VentasResumenProductoRow[],
  topN = 8
): { data: ChartRowMensual[]; series: string[] } {
  const buckets = groupRowsByMes(rowsMensuales);
  const totalPorRamo = montosPorCampo(rowsMensuales, 'nombreRamo');
  const { items, syntheticLabel } = topNConOtros(
    Array.from(totalPorRamo.entries()).map(([nombre, monto]) => ({ nombre, monto })),
    topN
  );
  const series = items.map((s) => s.nombre);
  const namedSeries = new Set(
    series.filter((s) => s !== syntheticLabel)
  );

  const data: ChartRowMensual[] = buckets.map((b) => {
    const porRamo = montosPorCampo(b.rows, 'nombreRamo');
    const row: ChartRowMensual = { [CHART_MES_KEY]: b.label };
    let resto = 0;
    for (const [nombre, monto] of Array.from(porRamo.entries())) {
      if (namedSeries.has(nombre)) {
        row[nombre] = (Number(row[nombre]) || 0) + monto;
      } else {
        resto += monto;
      }
    }
    if (syntheticLabel) {
      row[syntheticLabel] = (Number(row[syntheticLabel]) || 0) + resto;
    }
    for (const s of series) {
      if (row[s] == null) row[s] = 0;
    }
    return row;
  });

  return { data, series };
}

/** Barras por categoría dentro de un mes (línea o clase). */
export function buildBarrasPorCampoMes(
  rowsDelMes: VentasResumenProductoRow[],
  field: CampoClasificacion,
  topN = 10
): SerieBarraMes[] {
  const map = montosPorCampo(rowsDelMes, field);
  return topNConOtros(
    Array.from(map.entries()).map(([nombre, monto]) => ({ nombre, monto })),
    topN
  ).items;
}

/** Serie mensual de un producto seleccionado. */
export function buildSerieProductoMensual(
  rowsMensuales: VentasResumenProductoRow[],
  producto: VentasResumenProductoRow
): SerieBarraMes[] {
  const buckets = groupRowsByMes(rowsMensuales);
  return buckets.map((b) => {
    const monto = b.rows
      .filter((r) => isSameProductoResumenRow(r, producto))
      .reduce((acc, r) => acc + r.montoTotal, 0);
    return { nombre: b.label, monto };
  });
}
